import React from 'react'
import styled from 'styled-components'
import { sizes } from '../utils/breakpoints'
import { useMediaQuery } from 'react-responsive'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'
import LogoMobile from '../assets/images/logoTartisFooterMobile.svg'
import LogoDesktop from '../assets/images/logoTartisFooterDesktop.svg'
import Enlaces from './Enlaces'
import { InstagramIcon, LinkedinIcon, EnvelopeIcon } from './SocialIcons'
import { getLangFromPath, withLang } from '../utils/i18nRoutes'

const Footer = () => {
  const isMobile = useMediaQuery({ maxWidth: sizes.mobile })
  const { t } = useTranslation()
  const location = useLocation()
  const lang = getLangFromPath(location.pathname)
  const logo = isMobile ? LogoMobile : LogoDesktop
  const anio = new Date().getFullYear()

  return (
    <Container>
      <TopRow>
        <ImgLogo src={logo} alt="Logo TARTIS" />

        <LinksRow>
          <Enlaces to={withLang('/talento', lang)}>{t('footer.talento')}</Enlaces>
          <Enlaces to="/aviso-legal">{t('footer.avisoLegal')}</Enlaces>
          <Enlaces to={withLang('/site-map', lang)}>{t('footer.siteMap')}</Enlaces>
          <Enlaces to="/politica-privacidad">
            {t('footer.politicaPrivacidad')}
          </Enlaces>
          <Enlaces to="/canal-responsable">
            {t('footer.canalResponsable')}
          </Enlaces>
        </LinksRow>

        <IconsRow>
          <IconLink
            href="https://www.instagram.com/tartistechnologies/"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Instagram"
          >
            <InstagramIcon />
          </IconLink>
          <IconLink
            href="https://www.linkedin.com/company/tartis/posts/?feedView=all"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="LinkedIn"
          >
            <LinkedinIcon />
          </IconLink>
          <ContactRow href="mailto:info@tartis.io">
            <EnvelopeIcon />
            <ContactText>info@tartis.io</ContactText>
          </ContactRow>
        </IconsRow>
      </TopRow>

      <Separator />

      <Copyright>{t('footer.copyright', { year: anio })}</Copyright>
    </Container>
  )
}

export default Footer

const Container = styled.footer`
  background-color: ${({ theme }) => theme.colors.brand.dark};
  padding: 56px 40px 20px;

  @media (max-width: ${sizes.mobile}px) {
    padding: 40px 20px 16px;
  }
`

const TopRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  max-width: 1200px;
  margin: 0 auto;

  @media (max-width: ${sizes.mobile}px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
  }
`

const ImgLogo = styled.img`
  height: 24px;
  width: auto;
`

const LinksRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 20px;

  @media (max-width: ${sizes.mobile}px) {
    gap: 10px;
  }
`

const IconsRow = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`

const ContactRow = styled.a`
  display: flex;
  align-items: center;
  gap: 8px;
  color: ${({ theme }) => theme.colors.text.onDark};
  text-decoration: none;

  &:hover {
    opacity: 0.7;
  }
`

const ContactText = styled.span`
  font-family: ${({ theme }) => theme.fonts.accentRegular};
  font-size: 14px;

  @media (max-width: ${sizes.mobile}px) {
    display: none;
  }
`

const IconLink = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  opacity: 0.9;
  transition: opacity 0.15s ease;

  &:hover {
    opacity: 1;
  }
`

const Separator = styled.hr`
  height: 1px;
  border: none;
  background-color: rgba(255, 255, 255, 0.12);
  max-width: 1200px;
  box-sizing: border-box;
  margin: 20px auto;
`

const Copyright = styled.p`
  font-family: ${({ theme }) => theme.fonts.mono};
  font-size: 12px;
  color: rgba(255, 255, 255, 0.7);
  max-width: 1200px;
  margin: 0 auto;
`
